import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import { useDataInspector } from '../../providers/DataInspectorProvider';
import { useAuth } from '../../providers/AuthenticationProvider';
import { useNavigate } from 'react-router';
import { connectorMappingService } from '../../services/ConnectorMappingService';
import { pointMappingService } from '../../services/PointMappingService';
import moment from 'moment';
import './ConnectorPointMapping.css';

const WEB_PAGE = 'Connector Point Mapping';

const content = {
    marginTop: '2rem',
};

const BACNET_UNITS = {
    62: '°C',
    64: '°F',
    84: 'CFM',
    91: 'Percent',
    95: 'psi',
    98: '%RH',
    117: 'kW',
    118: 'kWh',
};

const formatBacnetUnits = (unitCode) => {
    if (unitCode === null || unitCode === undefined) return '';
    return BACNET_UNITS[unitCode] || `Unit ${unitCode}`;
};

export const ConnectorPointMapping = () => {
    const { checkAuth, getUserType, userType } = useAuth();
    const { createNumericPoint } = useDataInspector();
    const navigate = useNavigate();

    const [canSeeAllColumns, setCanSeeAllColumns] = useState(false);

    // Group selection
    const [groupId, setGroupId] = useState();
    const [groupOptions, setGroupOptions] = useState([]);

    // Building/Equipment selection (primary - for viewing equipment points)
    const [buildingId, setBuildingId] = useState();
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [equipmentId, setEquipmentId] = useState();
    const [equipmentOptions, setEquipmentOptions] = useState([]);

    // Connector selection (for BACnet points to map to)
    const [connectorId, setConnectorId] = useState();
    const [connectorOptions, setConnectorOptions] = useState([]);

    // Points data
    const [equipmentPoints, setEquipmentPoints] = useState([]);
    const [bacnetPoints, setBacnetPoints] = useState([]);

    // Numeric Point modal (for NP type points)
    const [selectedNumericPoint, setSelectedNumericPoint] = useState(null);
    const [numericPointTimestamp, setNumericPointTimestamp] = useState(moment().format('YYYY-MM-DDTHH:mm'));
    const [numericPointValue, setNumericPointValue] = useState(0);

    // Mapping modal (for non-NP points)
    const [selectedEquipmentPoint, setSelectedEquipmentPoint] = useState(null);
    const [selectedBacnetPointId, setSelectedBacnetPointId] = useState(null);

    const {
        getDataInspectorGroups,
        getDataInspectorBuildings,
        getDataInspectorEquipment,
        groups,
        buildings,
        equipment
    } = useDataInspector();

    // Auth check
    useEffect(() => {
        if (checkAuth(getUserType(), WEB_PAGE)) {
            navigate('/');
        }
    }, [checkAuth, getUserType, navigate]);

    useEffect(() => {
        if (userType === 'System Administrator' || userType === 'Pathian Technician') {
            setCanSeeAllColumns(true);
        } else {
            setCanSeeAllColumns(false);
        }
    }, [userType]);

    // Load groups
    useEffect(() => {
        getDataInspectorGroups();
    }, [getDataInspectorGroups]);

    useEffect(() => {
        const tempOptions = groups.map(group => ({
            key: group.id,
            value: group.groupId,
            text: group.name,
        }));
        setGroupOptions(tempOptions);
    }, [groups]);

    // Load buildings and connectors when group changes
    useEffect(() => {
        if (!groupId) {
            setBuildingOptions([]);
            setConnectorOptions([]);
            return;
        }

        getDataInspectorBuildings(groupId);

        const loadConnectors = async () => {
            try {
                const data = await connectorMappingService.getConnectors(groupId);
                const options = data.map(c => ({
                    key: c.id,
                    value: c.id,
                    text: `${c.connectorName} (${c.deviceCount} devices, ${c.pointCount} points)`,
                }));
                setConnectorOptions(options);
            } catch (e) {
                setConnectorOptions([]);
            }
        };
        loadConnectors();
    }, [groupId, getDataInspectorBuildings]);

    // Load buildings options
    useEffect(() => {
        const tempOptions = buildings.map(b => ({
            key: b.id,
            value: b.id,
            text: `(${b.id}) ${b.name}`,
        }));
        setBuildingOptions(tempOptions);
    }, [buildings]);

    // Load equipment when building changes
    useEffect(() => {
        if (!buildingId) {
            setEquipmentOptions([]);
            return;
        }
        getDataInspectorEquipment({ buildingIds: [buildingId] });
    }, [buildingId, getDataInspectorEquipment]);

    useEffect(() => {
        const tempOptions = equipment.map(e => ({
            key: e.id,
            value: e.id,
            text: `(${e.id}) ${e.name}`,
        }));
        setEquipmentOptions(tempOptions);
    }, [equipment]);

    // Load equipment points when equipment changes
    const loadEquipmentPoints = useCallback(async () => {
        if (!buildingId || !equipmentId) {
            setEquipmentPoints([]);
            return;
        }

        const data = await pointMappingService.viewEquipmentPoints(buildingId, equipmentId);
        setEquipmentPoints(data || []);
    }, [buildingId, equipmentId]);

    useEffect(() => {
        loadEquipmentPoints();
    }, [loadEquipmentPoints]);

    // Load BACnet points when connector changes (for mapping modal)
    useEffect(() => {
        if (!groupId || !connectorId) {
            setBacnetPoints([]);
            return;
        }

        const loadBacnetPoints = async () => {
            const data = await connectorMappingService.getPoints(groupId, connectorId, null, false);
            setBacnetPoints(data);
        };
        loadBacnetPoints();
    }, [groupId, connectorId]);

    // Handlers
    const handleGroupChange = (value) => {
        setGroupId(value);
        setBuildingId(null);
        setEquipmentId(null);
        setConnectorId(null);
        setEquipmentPoints([]);
        setBacnetPoints([]);
    };

    const handleBuildingChange = (value) => {
        setBuildingId(value);
        setEquipmentId(null);
    };

    const handleEquipmentChange = (value) => {
        setEquipmentId(value);
    };

    const handleConnectorChange = (value) => {
        setConnectorId(value);
    };

    // Numeric Point handlers
    const handleNumericPointModalCancel = () => {
        setSelectedNumericPoint(null);
        setNumericPointTimestamp(moment().format('YYYY-MM-DDTHH:mm'));
        setNumericPointValue(0);
    };

    const handleNumericPointModalSubmit = async () => {
        if (numericPointTimestamp === "" || numericPointValue === "") {
            toast({
                type: 'error',
                description: 'Numeric Point must have both a timestamp and a value',
                animation: 'fade down',
                time: 5000,
            });
            setSelectedNumericPoint(null);
            return;
        }
        const result = await createNumericPoint(selectedNumericPoint, moment.utc(numericPointTimestamp).format(), numericPointValue);
        if (result > 0) {
            toast({
                type: 'success',
                description: 'Equipment Numeric Point Value Assigned',
                animation: 'fade down',
                time: 5000,
            });
            loadEquipmentPoints();
            setSelectedNumericPoint(null);
            setNumericPointTimestamp(moment().format('YYYY-MM-DDTHH:mm'));
            setNumericPointValue(0);
        } else {
            toast({
                type: 'error',
                description: 'Equipment Numeric Point Value Assignment Failed',
                animation: 'fade down',
                time: 5000,
            });
        }
    };

    // Mapping handlers
    const handleMapPointClick = (equipmentPoint) => {
        setSelectedEquipmentPoint(equipmentPoint);
        // If already mapped to a BACnet point, pre-select it
        if (equipmentPoint.connectorPointId) {
            setSelectedBacnetPointId(equipmentPoint.connectorPointId);
        } else {
            setSelectedBacnetPointId(null);
        }
    };

    const handleMappingModalCancel = () => {
        setSelectedEquipmentPoint(null);
        setSelectedBacnetPointId(null);
    };

    const handleMappingModalSubmit = async () => {
        if (!selectedEquipmentPoint || !selectedBacnetPointId) {
            toast({
                type: 'error',
                description: 'Please select a BACnet point to map to',
                animation: 'fade down',
                time: 5000,
            });
            return;
        }

        const result = await connectorMappingService.mapPoint(
            groupId,
            selectedBacnetPointId,
            selectedEquipmentPoint.pointId
        );

        if (result) {
            toast({
                type: 'success',
                description: 'Equipment Point Mapping Updated',
                animation: 'fade down',
                time: 5000,
            });
            loadEquipmentPoints();
            setSelectedEquipmentPoint(null);
            setSelectedBacnetPointId(null);
        } else {
            toast({
                type: 'error',
                description: 'Equipment Point Mapping Update Failed',
                animation: 'fade down',
                time: 5000,
            });
        }
    };

    const handleMappingModalUnmap = async () => {
        if (!selectedEquipmentPoint?.connectorPointId) {
            return;
        }

        const result = await connectorMappingService.unmapPoint(groupId, selectedEquipmentPoint.connectorPointId);
        if (result) {
            toast({
                type: 'success',
                description: 'Equipment Point Unmapped',
                animation: 'fade down',
                time: 5000,
            });
            loadEquipmentPoints();
            setSelectedEquipmentPoint(null);
            setSelectedBacnetPointId(null);
        } else {
            toast({
                type: 'error',
                description: 'Equipment Point Unmap Failed',
                animation: 'fade down',
                time: 5000,
            });
        }
    };

    return (
        <div style={content}>
            {/* Numeric Point Modal */}
            <Modal
                closeIcon
                onClose={() => setSelectedNumericPoint(null)}
                open={selectedNumericPoint !== null}
                style={{ textAlign: 'left' }}
                size="tiny"
            >
                <Modal.Header>Edit Numeric Point Value</Modal.Header>
                <Modal.Content>
                    <Form id="numericPointForm">
                        {canSeeAllColumns && (
                            <Form.Field>
                                <label>Equipment Information</label>
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    {equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.equipmentTreeDisplayText}
                                </div>
                            </Form.Field>
                        )}
                        {canSeeAllColumns && (
                            <Form.Field>
                                <label>Point Code</label>
                                <div>{equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.pointCode}</div>
                            </Form.Field>
                        )}
                        <Form.Field>
                            <label>Point Description</label>
                            <div>{equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.pointDescription}</div>
                        </Form.Field>
                        <Form.Field>
                            <label>Point Timestamp</label>
                            <Form.Input
                                type="datetime-local"
                                defaultValue={moment().format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => setNumericPointTimestamp(e.target.value)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Point Value</label>
                            <Form.Input
                                name="value"
                                type="number"
                                defaultValue={0}
                                onChange={(e) => setNumericPointValue(e.target.value)}
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button type="button" onClick={handleNumericPointModalCancel}>Cancel</Button>
                    <Button type="button" onClick={handleNumericPointModalSubmit}>Submit</Button>
                </Modal.Actions>
            </Modal>

            {/* BACnet Point Mapping Modal */}
            <Modal
                closeIcon
                onClose={handleMappingModalCancel}
                open={selectedEquipmentPoint !== null}
                style={{ textAlign: 'left' }}
                size="fullscreen"
            >
                <Modal.Header>
                    Edit Point Mapping - {selectedEquipmentPoint?.pointDescription}
                </Modal.Header>
                <Modal.Content>
                    <Form id="pointMappingForm">
                        {canSeeAllColumns && (
                            <Form.Field>
                                <label>Equipment Information</label>
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    {selectedEquipmentPoint?.equipmentTreeDisplayText}
                                </div>
                            </Form.Field>
                        )}
                        {canSeeAllColumns && (
                            <Form.Field>
                                <label>Point Code</label>
                                <div>{selectedEquipmentPoint?.pointCode}</div>
                            </Form.Field>
                        )}
                        <Form.Field>
                            <label>Point Description</label>
                            <div>{selectedEquipmentPoint?.pointDescription}</div>
                        </Form.Field>
                        <Form.Field>
                            <label>BACnet Point (Device - Object Type - Object Instance - Name)</label>
                            <Form.Dropdown
                                placeholder="Select BACnet Point"
                                fluid
                                selection
                                search
                                value={selectedBacnetPointId}
                                onChange={(e, { value }) => setSelectedBacnetPointId(value)}
                                options={bacnetPoints.map(bp => ({
                                    key: bp.id,
                                    value: bp.id,
                                    text: `Device ${bp.bacnetDeviceId} - ${bp.objectType}:${bp.objectInstance} - ${bp.name || bp.objectIdentifier}`,
                                    description: bp.equipmentPointId ? 'Already mapped' : null
                                }))}
                                disabled={!connectorId}
                            />
                            {!connectorId && (
                                <p style={{ color: '#999', marginTop: '0.5rem' }}>
                                    Select a Connector above to see available BACnet points
                                </p>
                            )}
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {selectedEquipmentPoint?.connectorPointId && (
                        <Button type="button" color="orange" onClick={handleMappingModalUnmap}>
                            Unmap
                        </Button>
                    )}
                    <Button type="button" onClick={handleMappingModalCancel}>Cancel</Button>
                    <Button type="button" primary onClick={handleMappingModalSubmit}>Submit</Button>
                </Modal.Actions>
            </Modal>

            {/* Filters */}
            <div>
                <Form>
                    <Form.Group style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexBasis: '80%' }}>
                            <Form.Dropdown
                                width={4}
                                style={{ height: '38px' }}
                                value={groupId}
                                label="Group"
                                placeholder="Select Group"
                                options={groupOptions}
                                onChange={(e, { value }) => handleGroupChange(value)}
                                selection
                                search
                                required
                            />
                            <Form.Dropdown
                                width={4}
                                style={{ height: '38px' }}
                                value={buildingId}
                                label="Building"
                                placeholder="Select Building"
                                options={buildingOptions}
                                onChange={(e, { value }) => handleBuildingChange(value)}
                                selection
                                search
                                clearable
                                disabled={!groupId}
                            />
                            <Form.Dropdown
                                width={4}
                                style={{ height: '38px' }}
                                value={equipmentId}
                                label="Equipment"
                                placeholder="Select Equipment"
                                options={equipmentOptions}
                                onChange={(e, { value }) => handleEquipmentChange(value)}
                                selection
                                search
                                clearable
                                disabled={!buildingId}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexBasis: '80%' }}>
                            <Form.Dropdown
                                width={4}
                                style={{ height: '38px' }}
                                value={connectorId}
                                label="Connector (for BACnet mapping)"
                                placeholder="Select Connector"
                                options={connectorOptions}
                                onChange={(e, { value }) => handleConnectorChange(value)}
                                selection
                                search
                                clearable
                                disabled={!groupId}
                            />
                        </div>
                    </Form.Group>
                </Form>
            </div>

            {/* Equipment Points Grid */}
            <div style={{ width: canSeeAllColumns ? '80vw' : '40vw' }}>
                <Grid data={equipmentPoints}>
                    {canSeeAllColumns && <GridColumn field="equipmentId" title="Equipment Id" />}
                    {canSeeAllColumns && <GridColumn field="equipmentName" title="Equipment Name" />}
                    {canSeeAllColumns && <GridColumn field="pointCode" title="Point Code" />}
                    <GridColumn field="pointDescription" title="Description" />
                    {canSeeAllColumns && <GridColumn field="pointType" title="Point Type" />}
                    {canSeeAllColumns && <GridColumn field="units" title="Units" />}
                    <GridColumn
                        field="value"
                        title="Value"
                        cell={props => (
                            <td>
                                {props.dataItem.pointType === 'NP' ? (
                                    props.dataItem.value ?? 'null'
                                ) : (
                                    props.dataItem.value === null ? (
                                        props.dataItem.connectorPointId ? (
                                            "Point Mapped - No Data Received Yet"
                                        ) : (
                                            "Point Unmapped"
                                        )
                                    ) : (
                                        props.dataItem.value
                                    )
                                )}
                            </td>
                        )}
                    />
                    <GridColumn
                        title="Actions"
                        cell={props => (
                            <td>
                                {props.dataItem.pointType === 'NP' ? (
                                    <Button
                                        type="button"
                                        size="mini"
                                        onClick={() => setSelectedNumericPoint(props.dataItem.pointId)}
                                    >
                                        Set Value
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        size="mini"
                                        primary
                                        onClick={() => handleMapPointClick(props.dataItem)}
                                    >
                                        Map Point
                                    </Button>
                                )}
                            </td>
                        )}
                    />
                </Grid>
            </div>

            <SemanticToastContainer position="bottom-center" />
        </div>
    );
};
